<script lang="ts" setup>
import type { ComponentOptions, ConcreteComponent } from 'vue'
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import Flicking from '@egjs/vue3-flicking'
import '@egjs/vue3-flicking/dist/flicking.css'
import type { FlickingOptions, WillChangeEvent } from '@egjs/vue3-flicking'
import { AutoPlay, Fade, Parallax, Perspective } from '@egjs/flicking-plugins'
import type { Media, Notebook, Repo, Skill } from '@prisma/client'
import { Iconify } from '~/types/assets'
import type { Theme } from '~/types/theme'

const props = defineProps<{
  slides: (Repo | Media | Skill | Notebook | Theme)[]
  component: ConcreteComponent
  fade?: boolean
  autoPlay?: boolean
  parallax?: boolean
  perspective?: boolean
  slideProps?: Record<string, unknown>
  slideClasses?: string
}>()

const flicking = ref<Flicking | null>(null)

const activeIndex = ref(0)
const minimal = ref(false)
const plugins = []

if (props.fade) plugins.push(new Fade())
if (props.autoPlay) plugins.push(new AutoPlay({ duration: 5000, direction: 'NEXT', stopOnHover: true }))
if (props.parallax) plugins.push(new Parallax())
if (props.perspective) plugins.push(new Perspective({ rotate: 0.5 }))

const slider: Partial<FlickingOptions & ComponentOptions> = {
  defaultIndex: activeIndex.value,
  viewportTag: 'div',
  cameraTag: 'div',
  cameraClass: '',
  plugins
}

function onChange({ index }: WillChangeEvent) {
  activeIndex.value = index
}

function onExpand(status: boolean) {
  minimal.value = status
}

function playNext(): void {
  flicking.value?.next()
}

function playPrev(): void {
  flicking.value?.prev()
}

function playIndex(index: number): void {
  flicking.value?.moveTo(index)
}
</script>

<template lang="pug">
.slider
  .slider__flicking
    Flicking(
      v-bind="slider"
      ref="flicking"
      data-test="flicking"
      :options="{ circular: true }"
      :prevent-events-before-init="true"
      :render-only-visible="true"
      :hide-before-init="true"
      @will-change="onChange"
    )
      .slider__flicking__slide(
        v-for="(slide, index) in slides"
        :key="`slide-${index}`"
        :class="slideClasses"
      )
        Component(
          :is="component",
          :repo="slide"
          :media="slide"
          :skill="slide"
          :document="slide"
          :theme="slide"
          :index="index"
          :lazy="index > 5"
          v-bind="slideProps"
          @on-expand="onExpand"
        )
  Transition(name="fade-left")
    .slider__controller-left(
      v-if="!minimal"
      data-test="play-prev"
      @click="playPrev"
    )
      Icon(:name="Iconify.left")
  Transition(name="fade-right")
    .slider__controller-right(
      v-if="!minimal"
      data-test="play-next"
      @click="playNext"
    )
      Icon(:name="Iconify.right")
  .slider__controllers(
    v-if="!(slides.length >= 30)"
  )
    .slider__controllers__controller(
      v-for="(slide, index) in slides"
      :key="`controller-${index}`"
      data-test="controller"
      :class="index === activeIndex ? 'bg-primary' : 'bg-dark-600'"
      @click="playIndex(index)"
    )
</template>

<style lang="sass" scoped>
.slider
  @apply w-full border-x border-primary relative
  span.iconify
    @apply bg-dark-900
  .slider__flicking
    @apply w-full overflow-hidden
    :deep(.flicking-viewport) .flicking-camera
      @apply items-center
    .slider__flicking__slide
      @apply h-full
  .slider__controller-left,
  .slider__controller-right
    @apply absolute top-[50%] translate-y-[-50%] bg-primary px-0 py-6 text-dark-900 cursor-pointer z-10
  .slider__controller-right
    @apply right-0
  .slider__controller-left
    @apply left-0
  .slider__controllers
    @apply absolute -bottom-4 w-full flex-center gap-3 overflow-auto translate-y-[100%] scrollbar-primary
    &::-webkit-scrollbar
      @apply h-2
    .slider__controllers__controller
      @apply w-4 h-4 rounded-[50%] cursor-pointer flex-shrink-0
</style>
